import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Cancel,
  DirectionsBus,
  CalendarMonth,
  Schedule,
  LocationOn,
  Person,
  Email,
  Phone,
  ConfirmationNumber,
  Groups,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

interface BookingDetails {
  booking: {
    id: string;
    guest_email: string;
    guest_name: string;
    guest_nickname: string | null;
    seats_booked: number;
    booking_code: string;
    phone: string | null;
    notes: string | null;
    status: string;
    created_at: string;
  };
  schedule: {
    id: string;
    departure_date: string;
    departure_time: string;
    pickup_location: string;
    destination: string;
    price: number | null;
  };
  shuttle: {
    name: string;
    capacity: number;
  };
  club: {
    club_name: string;
  };
}

const ShuttleBookingDetailsPage: React.FC = () => {
  const { bookingCode } = useParams<{ bookingCode: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelPin, setCancelPin] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  const token = searchParams.get('token');

  useEffect(() => {
    fetchBooking();
  }, [bookingCode]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      // Include token in the request if available
      const url = token 
        ? `${apiUrl}/public/bookings/${bookingCode}?token=${encodeURIComponent(token)}`
        : `${apiUrl}/public/bookings/${bookingCode}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(t('shuttle.booking.invalidLink'));
        }
        if (response.status === 404) {
          throw new Error(t('shuttle.booking.notFound'));
        }
        throw new Error(t('shuttle.errors.fetchBooking'));
      }
      
      const data = await response.json();
      setBooking(data);
    } catch (error: any) {
      console.error('Failed to fetch booking:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelPin || cancelPin.length !== 4) {
      setError(t('shuttle.booking.enterPin'));
      return;
    }

    try {
      setCancelling(true);
      const response = await fetch(`${apiUrl}/public/bookings/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_code: bookingCode,
          pin_code: cancelPin,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('shuttle.errors.cancelFailed'));
      }
      
      await response.json();
      
      // Refresh booking data
      await fetchBooking();
      setCancelDialogOpen(false);
      setCancelPin('');
      
      // Show success message
      setError(null);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !booking) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('shuttle.errors.dataNotFound')}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="outlined"
        >
          {t('common.backToHome')}
        </Button>
      </Container>
    );
  }

  const departureDate = new Date(booking.schedule.departure_date);
  const isCancelled = booking.booking.status === 'cancelled';
  const canCancel = !isCancelled && new Date() < departureDate;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4">
              {t('shuttle.booking.code')}: {booking.booking.booking_code}
            </Typography>
            <Chip
              label={isCancelled ? t('shuttle.booking.cancelled') : t('shuttle.booking.confirmed')}
              color={isCancelled ? 'error' : 'success'}
              size="medium"
            />
          </Box>

        {/* Booking Code */}
        <Card sx={{ mb: 3, backgroundColor: 'primary.50' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ConfirmationNumber sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('shuttle.booking.code')}
                </Typography>
                <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
                  {booking.booking.booking_code}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Digital Payment Coming Soon */}
        <Box sx={{ 
          mb: 3,
          p: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          textAlign: 'center'
        }}>
          <Typography sx={{ 
            fontSize: '0.85rem', 
            color: 'text.secondary',
            mb: 1,
            fontWeight: 500
          }}>
            💳 {t('registration.digitalPayment', { defaultValue: 'Digital Payment' })} - {t('registration.comingSoon', { defaultValue: 'Coming Soon' })}
          </Typography>
          <Box sx={{ 
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.4
          }}>
            {/* Visa */}
            <Box sx={{ 
              height: 24,
              width: 40,
              bgcolor: '#1A1F71',
              borderRadius: 0.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 'bold' }}>VISA</Typography>
            </Box>
            {/* Mastercard */}
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EB001B' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#F79E1B', ml: -0.4 }} />
            </Box>
            {/* Apple Pay */}
            <Box sx={{ 
              height: 24,
              px: 0.7,
              bgcolor: 'black',
              borderRadius: 0.4,
              display: 'flex',
              alignItems: 'center',
              gap: 0.3
            }}>
              <Typography sx={{ color: 'white', fontSize: '0.65rem' }}>🍎</Typography>
              <Typography sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 500 }}>Pay</Typography>
            </Box>
            {/* Google Pay */}
            <Box sx={{ 
              height: 24,
              px: 0.7,
              bgcolor: 'white',
              border: '1px solid #dadce0',
              borderRadius: 0.4,
              display: 'flex',
              alignItems: 'center'
            }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#3c4043' }}>G Pay</Typography>
            </Box>
          </Box>
        </Box>

        {/* Shuttle Details */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('shuttle.booking.shuttleDetails')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DirectionsBus sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>
                    {booking.shuttle.name} - {booking.club.club_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>
                    {format(departureDate, 'EEEE, MMMM d, yyyy')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>
                    {booking.schedule.departure_time.substring(0, 5)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">
                      {t('shuttle.from')}: {booking.schedule.pickup_location || t('shuttle.defaultPickup')}
                    </Typography>
                    <Typography variant="body2">
                      {t('shuttle.to')}: {booking.schedule.destination || t('shuttle.defaultDestination')}
                    </Typography>
                  </Box>
                </Box>
                {/* Fee Display */}
                <Box sx={{ mt: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      display: 'inline-block',
                      p: 1.5,
                      background: booking.schedule.price && booking.schedule.price > 0
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
                      color: 'white',
                      borderRadius: 2,
                      width: '100%',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.9 }}>
                      {t('shuttle.booking.totalPrice', { defaultValue: 'Total Amount' })}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {booking.schedule.price && booking.schedule.price > 0 
                        ? `€${(booking.schedule.price * booking.booking.seats_booked).toFixed(2)}` 
                        : t('registration.free', { defaultValue: 'FREE' })}
                    </Typography>
                    {booking.booking.seats_booked > 1 && booking.schedule.price && booking.schedule.price > 0 && (
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, mt: 0.5 }}>
                        {booking.booking.seats_booked} seats × €{booking.schedule.price.toFixed(2)}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </CardContent>
            </Card>

          <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('shuttle.booking.passengerDetails')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>
                    {booking.booking.guest_name}
                    {booking.booking.guest_nickname && ` (${booking.booking.guest_nickname})`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>{booking.booking.guest_email}</Typography>
                </Box>
                {booking.booking.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography>{booking.booking.phone}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Groups sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography>
                    {booking.booking.seats_booked} {booking.booking.seats_booked > 1 ? t('shuttle.passengers.seats') : t('shuttle.passengers.seat')}
                  </Typography>
                </Box>
                {booking.booking.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('shuttle.booking.notes')}
                    </Typography>
                    <Typography variant="body2">
                      {booking.booking.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            variant="outlined"
          >
            {t('common.backToHome')}
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`/shuttle/${booking.schedule.id}/passengers`)}
            >
              {t('shuttle.viewPassengers')}
            </Button>
            {canCancel && (
              <Button
                startIcon={<Cancel />}
                onClick={() => setCancelDialogOpen(true)}
                variant="outlined"
                color="error"
              >
                {t('shuttle.booking.cancel')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelPin('');
          setError(null);
        }}
      >
        <DialogTitle>
          {t('shuttle.booking.cancelConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('shuttle.booking.cancelConfirmText')}
          </DialogContentText>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label={t('shuttle.booking.pinRequired')}
            value={cancelPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCancelPin(value);
            }}
            type="password"
            inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
            helperText={t('shuttle.booking.pinHelp')}
            error={error?.includes('PIN') || false}
            autoFocus
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('shuttle.booking.refundInfo')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCancelDialogOpen(false);
            setCancelPin('');
            setError(null);
          }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCancelBooking}
            color="error"
            variant="contained"
            disabled={cancelling || cancelPin.length !== 4}
          >
            {cancelling ? <CircularProgress size={24} /> : t('shuttle.booking.confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShuttleBookingDetailsPage;